// Compact selectable row used inside config panel sections.
//
// Layout:  [ check ] [ thumb ] [ name + sub + hint ]            [ price ]
//
// Image slot:
//   • Pass `imageUrl` for the slot to attempt loading that file
//   • Pass `monogram` for the placeholder letter (e.g. first letter of name)
//   • If the image 404s, the placeholder takes over
//
// Locked states:
//   • `included` — visible but greyed, "Included" badge, contributes $0
//   • `disabled` — visible but greyed, ignores clicks (reserved for future)

import { useEffect, useState } from "react";

import { formatDollars } from "../../format";

interface Props {
  name: string;
  sub?: string;
  priceCents: number;
  selected: boolean;
  onClick: () => void;
  inputType: "radio" | "checkbox";
  /** Ordered list of image URLs to try. Walks the list on 404, falls back
   *  to the monogram placeholder if all fail. */
  imageCandidates?: string[];
  /** Letter or 2-letter monogram for the placeholder when no image loads. */
  monogram?: string;
  included?: boolean;
  disabled?: boolean;
  hint?: string;
  strikethroughOriginalCents?: number;
  /** When provided AND `detailOnRowClick` is false (default), the
   *  thumbnail becomes a separate inner button that opens the detail
   *  modal. When `detailOnRowClick` is true, the WHOLE row body opens
   *  the modal instead. */
  onThumbClick?: () => void;
  /** Override the dollar-formatted price label. Used by the Machine
   *  section in Finance pricing mode to show e.g. "$543 /mo" instead of
   *  "$45,999". The strikethrough + included logic still apply on top. */
  priceLabel?: string;
  /** When true, clicking the row body (thumb + name + price) opens the
   *  detail modal (via onThumbClick), and ONLY the checkbox toggles
   *  selection (via onClick). When false (default), the whole row
   *  toggles and the thumb is the only modal trigger. Used by the
   *  Accessories section so the larger hit target matches customer
   *  expectations of "the whole row is clickable for details." */
  detailOnRowClick?: boolean;
}

export function OptionRow({
  name,
  sub,
  priceCents,
  selected,
  onClick,
  inputType,
  imageCandidates,
  monogram,
  included = false,
  disabled = false,
  hint,
  strikethroughOriginalCents,
  onThumbClick,
  priceLabel,
  detailOnRowClick = false,
}: Props) {
  const locked = included || disabled;

  const check = (
    <span
      aria-hidden
      className={
        "flex h-5 w-5 flex-none items-center justify-center border-2 transition " +
        (inputType === "radio" ? "rounded-full" : "") +
        " " +
        (selected || included
          ? "border-amber bg-amber"
          : "border-line bg-white")
      }
    >
      {(selected || included) && inputType === "checkbox" && (
        <svg
          viewBox="0 0 16 16"
          className="h-3.5 w-3.5"
          fill="none"
          stroke="#1a1a1a"
          strokeWidth="2.5"
          strokeLinecap="square"
        >
          <path d="M3 8.5 L7 12 L13 4" />
        </svg>
      )}
      {(selected || included) && inputType === "radio" && (
        <span className="h-2 w-2 rounded-full bg-ink" />
      )}
    </span>
  );

  const body = (
    <>
      {/* Thumbnail — interactive (zoom cursor + hover dim) only in the
          OLD mode, where the row click is "select" and the thumbnail
          is the only modal trigger. In the new detailOnRowClick mode,
          the whole row body opens the modal so the thumb is just an
          image — no nested-button trickery. */}
      <Thumb
        candidates={imageCandidates ?? []}
        monogram={monogram ?? name.slice(0, 1).toUpperCase()}
        alt={name}
        onClick={detailOnRowClick ? undefined : onThumbClick}
        // Show the ⓘ badge whenever the row has ANY modal affordance:
        // direct-thumb-click (machines) OR whole-row-click (accessories).
        showInfoBadge={Boolean(onThumbClick) || detailOnRowClick}
      />

      {/* Name + meta */}
      <div className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-2">
          <span className="font-display text-base font-extrabold uppercase tracking-wide text-ink break-words">
            {name}
          </span>
          {included && (
            <span className="bg-amber-50 px-1.5 py-0.5 font-sans text-[12px] font-bold uppercase tracking-tag text-ink">
              Included
            </span>
          )}
        </div>
        {sub && (
          <div className="font-sans text-[14px] font-semibold uppercase tracking-tag text-steel">
            {sub}
          </div>
        )}
        {hint && (
          <div className="mt-0.5 font-sans text-[14px] italic text-steel">
            {hint}
          </div>
        )}
      </div>

      {/* Price */}
      <div className="flex flex-none items-baseline gap-2 font-display text-sm font-extrabold text-ink sm:text-base">
        {strikethroughOriginalCents && (
          <span className="font-sans text-sm font-normal text-steel line-through">
            {formatDollars(strikethroughOriginalCents)}
          </span>
        )}
        <span>
          {included ? "$0" : priceLabel ?? formatDollars(priceCents)}
        </span>
      </div>
    </>
  );

  const rowClass =
    "group flex w-full items-center gap-3 border-b border-dashed border-line py-3 transition last:border-b-0 " +
    (locked ? "cursor-default opacity-60" : selected ? "" : "hover:bg-mist/60");

  // ── Split-click mode (Accessories) ───────────────────────────────
  // Outer is a div (avoids nesting <button> in <button>). Two child
  // buttons: the checkbox toggles selection, the rest of the row opens
  // the detail modal. Behavior:
  //   • Click checkbox → onClick (toggle)
  //   • Click anywhere else on the row → onThumbClick (modal)
  if (detailOnRowClick) {
    return (
      <div className={rowClass}>
        <button
          type="button"
          onClick={locked ? undefined : onClick}
          aria-pressed={selected}
          aria-label={selected ? `Remove ${name}` : `Add ${name}`}
          disabled={locked}
          className="flex flex-none items-center justify-center"
        >
          {check}
        </button>
        <button
          type="button"
          onClick={onThumbClick}
          aria-label={`See ${name} details`}
          className="group flex flex-1 cursor-pointer items-center gap-3 text-left"
        >
          {body}
        </button>
      </div>
    );
  }

  // ── Whole-row-toggles mode (default — Machines, etc.) ────────────
  return (
    <button
      type="button"
      onClick={locked ? undefined : onClick}
      aria-pressed={selected}
      aria-disabled={locked}
      disabled={locked}
      className={rowClass + " text-left"}
    >
      {check}
      {body}
    </button>
  );
}

interface ThumbProps {
  candidates: string[];
  monogram: string;
  alt: string;
  onClick?: () => void;
  /** Show the ⓘ info badge in the corner — signposts to customers
   *  that the thumbnail (or its parent row) opens a detail modal. */
  showInfoBadge?: boolean;
}

function Thumb({ candidates, monogram, alt, onClick, showInfoBadge }: ThumbProps) {
  const [idx, setIdx] = useState(0);
  // Show the hatch + monogram placeholder ONLY until an image actually
  // loads — otherwise a transparent PNG cutout lets the monogram peek
  // through its edges.
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setIdx(0);
    setLoaded(false);
  }, [candidates.join("|")]);
  const url = candidates[idx];

  // When onClick is provided, render as a button (stops propagation so
  // the parent row doesn't also toggle selection). Otherwise a plain div.
  const Wrap = onClick ? "button" : "div";
  const interactive = Boolean(onClick);

  return (
    <Wrap
      type={onClick ? "button" : undefined}
      onClick={
        onClick
          ? (e: React.MouseEvent) => {
              e.stopPropagation();
              onClick();
            }
          : undefined
      }
      aria-label={onClick ? `View ${alt} details` : undefined}
      className={
        "group/thumb relative h-11 w-11 flex-none overflow-hidden border border-line bg-mist sm:h-16 sm:w-16 " +
        (interactive
          ? "cursor-zoom-in transition hover:border-amber"
          : "")
      }
    >
      {!loaded && (
        <>
          <div
            aria-hidden
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, transparent 0 8px, #E4E4E4 8px 9px)",
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="select-none font-display text-xl font-extrabold uppercase tracking-tight text-amber-700 sm:text-2xl">
              {monogram}
            </span>
          </div>
        </>
      )}
      {url && (
        <img
          key={url}
          src={url}
          alt={alt}
          onLoad={() => setLoaded(true)}
          onError={() => {
            setLoaded(false);
            setIdx((i) => i + 1);
          }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      {interactive && (
        <span
          aria-hidden
          className="absolute inset-0 flex items-center justify-center bg-ink/40 opacity-0 transition group-hover/thumb:opacity-100"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5 text-white"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
        </span>
      )}
      {showInfoBadge && <InfoBadge />}
    </Wrap>
  );
}

/** Small lowercase italic "i" in a white circle, parked in the
 *  thumbnail's bottom-right corner. Customer-facing signal that the
 *  row opens a detail modal — visible without hovering, so the
 *  affordance is discoverable on touch and keyboard too. */
function InfoBadge() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute bottom-1 right-1 z-10 inline-flex h-4 w-4 items-center justify-center rounded-full bg-white font-serif text-[11px] font-semibold italic leading-none text-ink shadow-[0_1px_3px_rgba(0,0,0,0.35)] sm:h-5 sm:w-5 sm:text-[13px]"
    >
      i
    </span>
  );
}
